// Drill-down hierarchy model.
//
// A single synthetic student roster is the source of truth for both the three
// headline metric cards and the drillable breakdown table. Any drill path is
// just a set of constraints over that roster; metrics for the cards and the
// child rows are aggregated from whatever slice the path selects. This lets the
// user drill by any dimension, in any order, to arbitrary depth, and keeps the
// cards and the table perfectly in sync with the breadcrumb.

import { SCHOOL_NAMES, TEACHERS_BY_SCHOOL, CATEGORIES } from "./data";

// ---- Dimensions ------------------------------------------------------------
export type DimKey =
  | "site"
  | "grade"
  | "teacher"
  | "coteacher"
  | "category"
  | "student";

export interface DimMeta {
  key: DimKey;
  noun: string; // column header / dropdown label
  by: string; // section title, e.g. "By teacher"
}

export const DIMENSIONS: DimMeta[] = [
  { key: "site", noun: "Site", by: "By school" },
  { key: "grade", noun: "Grade", by: "By grade" },
  { key: "teacher", noun: "Teacher", by: "By teacher" },
  { key: "coteacher", noun: "Co-teacher", by: "By co-teacher" },
  { key: "category", noun: "Category", by: "By category" },
  { key: "student", noun: "Student", by: "By student" },
];

export const DIM_BY_KEY: Record<DimKey, DimMeta> = DIMENSIONS.reduce(
  (acc, d) => ((acc[d.key] = d), acc),
  {} as Record<DimKey, DimMeta>,
);

// Default breakdown dimension for a level: first unused dimension in this
// preference order. At the root this yields "teacher", matching today's view.
const DEFAULT_PREFERENCE: DimKey[] = [
  "teacher",
  "grade",
  "site",
  "category",
  "coteacher",
  "student",
];

// ---- Drill path ------------------------------------------------------------
export interface DrillSegment {
  dim: DimKey;
  value: string; // canonical value used to match the roster
  label: string; // display label for the breadcrumb
}

export const ROOT_LABEL = "All sites";

// ---- Roster generation -----------------------------------------------------
export interface HStudent {
  id: string;
  name: string;
  site: string;
  grade: number;
  teacher: string;
  coteacher: string;
  category: string;
  active: boolean;
  minPerDay: number; // drives Average Time per Day card
  avgHours: number; // cumulative period hours, drives the table column
  onTrack: boolean;
}

interface SiteConf {
  students: number;
  activeRate: number;
  minMean: number;
  onTrackOfActive: number;
}

// Tuned so the district aggregate lands on the familiar headline numbers:
// 1,284 lifetime students, ~72% active, ~12 min/day, ~64% on track.
const SITE_CONF: Record<string, SiteConf> = {
  "Greater Dayton School": {
    students: 512,
    activeRate: 0.74,
    minMean: 12.5,
    onTrackOfActive: 0.66,
  },
  "Riverside Middle": {
    students: 388,
    activeRate: 0.69,
    minMean: 11.0,
    onTrackOfActive: 0.6,
  },
  "Oakwood Charter": {
    students: 240,
    activeRate: 0.74,
    minMean: 13.0,
    onTrackOfActive: 0.7,
  },
  "St. Anne's Academy": {
    students: 144,
    activeRate: 0.68,
    minMean: 11.5,
    onTrackOfActive: 0.62,
  },
};

const TEACHER_GRADE: Record<string, number> = {
  "Katie Andorka": 6,
  "Dana Whitfield": 7,
  "Marcus Bell": 8,
  "Linda J.": 7,
};

const COTEACHERS_BY_SCHOOL: Record<string, string[]> = {
  "Greater Dayton School": ["Sam Ortiz", "Rachel Kim"],
  "Riverside Middle": ["Tom Nguyen", "Beth Carter"],
  "Oakwood Charter": ["Rachel Kim", "Omar Haddad"],
  "St. Anne's Academy": ["Beth Carter", "Grace Lin"],
};

const CAT_WEIGHTS: [string, number][] = [
  ["Tier 1", 0.55],
  ["Tier 2", 0.22],
  ["Tier 3", 0.13],
  ["SPED", 0.1],
];

const FIRST_NAMES = [
  "Aisha",
  "Diego",
  "Priya",
  "Grace",
  "Liam",
  "Hana",
  "Noah",
  "Sofia",
  "Mateo",
  "Emma",
  "Jordan",
  "Maya",
  "Owen",
  "Zoe",
  "Eli",
  "Ava",
  "Caleb",
  "Nina",
  "Sam",
  "Lena",
  "Kofi",
  "Leo",
  "Mia",
  "Ruby",
  "Theo",
  "Omar",
  "Nia",
  "Ivy",
  "Nora",
  "Kai",
  "Ethan",
  "Jaylen",
  "Isla",
  "Marcus",
  "Beth",
  "Tomas",
  "Rania",
  "Devon",
];
const LAST_INITIALS = [
  "K.",
  "M.",
  "S.",
  "T.",
  "O.",
  "W.",
  "D.",
  "L.",
  "R.",
  "B.",
  "P.",
  "F.",
  "H.",
  "C.",
  "N.",
  "A.",
  "J.",
  "G.",
];

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const clamp = (n: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, n));

function altGrade(pg: number, rng: () => number): number {
  const opts = [6, 7, 8].filter((g) => g !== pg);
  return opts[Math.floor(rng() * opts.length)];
}

function pickCategory(rng: () => number): string {
  let x = rng();
  for (const [c, w] of CAT_WEIGHTS) {
    if (x < w) return c;
    x -= w;
  }
  return "Tier 1";
}

function genRoster(): HStudent[] {
  const rng = mulberry32(20260722);
  const out: HStudent[] = [];
  let idc = 0;
  for (const site of SCHOOL_NAMES) {
    const conf = SITE_CONF[site];
    const teachers = TEACHERS_BY_SCHOOL[site] || [];
    const coteachers = COTEACHERS_BY_SCHOOL[site] || ["—"];
    for (let i = 0; i < conf.students; i++) {
      const teacher = teachers[Math.floor(rng() * teachers.length)];
      const coteacher = coteachers[Math.floor(rng() * coteachers.length)];
      const pg = TEACHER_GRADE[teacher] ?? 7;
      const grade = rng() < 0.72 ? pg : altGrade(pg, rng);
      const category = pickCategory(rng);
      const active = rng() < conf.activeRate;
      const minPerDay = clamp(
        Math.round(conf.minMean + (rng() * 2 - 1) * 4),
        2,
        27,
      );
      const avgHours = clamp(+(6 + (rng() * 2 - 1) * 2.3).toFixed(1), 0.4, 12);
      const onTrack = active && rng() < conf.onTrackOfActive;
      const name = `${FIRST_NAMES[Math.floor(rng() * FIRST_NAMES.length)]} ${LAST_INITIALS[Math.floor(rng() * LAST_INITIALS.length)]}`;
      out.push({
        id: `s${idc++}`,
        name,
        site,
        grade,
        teacher,
        coteacher,
        category,
        active,
        minPerDay,
        avgHours,
        onTrack,
      });
    }
  }
  return out;
}

export const ROSTER: HStudent[] = genRoster();

// ---- Scope + metrics -------------------------------------------------------
function matches(s: HStudent, seg: DrillSegment): boolean {
  switch (seg.dim) {
    case "site":
      return s.site === seg.value;
    case "grade":
      return String(s.grade) === seg.value;
    case "teacher":
      return s.teacher === seg.value;
    case "coteacher":
      return s.coteacher === seg.value;
    case "category":
      return s.category === seg.value;
    case "student":
      return s.id === seg.value;
  }
}

export function scopeStudents(path: DrillSegment[]): HStudent[] {
  if (path.length === 0) return ROSTER;
  return ROSTER.filter((s) => path.every((seg) => matches(s, seg)));
}

export interface ScopeMetrics {
  total: number;
  activeCount: number;
  activePct: number;
  avgMin: number;
  onTrackCount: number;
  onTrackPct: number;
  avgHours: number;
}

export function metricsFor(students: HStudent[]): ScopeMetrics {
  const total = students.length;
  if (total === 0) {
    return {
      total: 0,
      activeCount: 0,
      activePct: 0,
      avgMin: 0,
      onTrackCount: 0,
      onTrackPct: 0,
      avgHours: 0,
    };
  }
  let activeCount = 0;
  let onTrackCount = 0;
  let minSum = 0;
  let hoursSum = 0;
  for (const s of students) {
    if (s.active) activeCount++;
    if (s.onTrack) onTrackCount++;
    minSum += s.minPerDay;
    hoursSum += s.avgHours;
  }
  return {
    total,
    activeCount,
    activePct: Math.round((activeCount / total) * 100),
    avgMin: Math.round(minSum / total),
    onTrackCount,
    onTrackPct:
      activeCount > 0 ? Math.round((onTrackCount / activeCount) * 100) : 0,
    avgHours: +(hoursSum / total).toFixed(1),
  };
}

// ---- Available dimensions + defaults ---------------------------------------
export function usedDims(path: DrillSegment[]): DimKey[] {
  return path.map((p) => p.dim);
}

export function availableDims(path: DrillSegment[]): DimMeta[] {
  const used = usedDims(path);
  return DIMENSIONS.filter((d) => !used.includes(d.key));
}

export function pickDefaultDim(path: DrillSegment[]): DimKey {
  const used = usedDims(path);
  for (const k of DEFAULT_PREFERENCE) if (!used.includes(k)) return k;
  return "student";
}

// ---- Breakdown rows --------------------------------------------------------
export interface BreakdownRow {
  dim: DimKey;
  value: string;
  label: string;
  sublabel: string;
  drillable: boolean;
  metrics: ScopeMetrics;
  delta: number;
}

const STUDENT_ROW_CAP = 30;

function accessor(dim: DimKey): (s: HStudent) => string {
  switch (dim) {
    case "site":
      return (s) => s.site;
    case "grade":
      return (s) => String(s.grade);
    case "teacher":
      return (s) => s.teacher;
    case "coteacher":
      return (s) => s.coteacher;
    case "category":
      return (s) => s.category;
    case "student":
      return (s) => s.id;
  }
}

function dominantSite(students: HStudent[]): string {
  const counts: Record<string, number> = {};
  for (const s of students) counts[s.site] = (counts[s.site] || 0) + 1;
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "";
}

function gradeRange(students: HStudent[]): string {
  const gs = Array.from(new Set(students.map((s) => s.grade))).sort(
    (a, b) => a - b,
  );
  if (gs.length === 0) return "";
  if (gs.length === 1) return `Gr ${gs[0]}`;
  return `Gr ${gs[0]}-${gs[gs.length - 1]}`;
}

export function teacherDominantSite(teacher: string): string {
  return dominantSite(ROSTER.filter((s) => s.teacher === teacher));
}

export function breakdownRows(
  students: HStudent[],
  dim: DimKey,
): BreakdownRow[] {
  const key = accessor(dim);

  if (dim === "student") {
    const rows = [...students]
      .sort((a, b) => b.avgHours - a.avgHours)
      .slice(0, STUDENT_ROW_CAP)
      .map<BreakdownRow>((s) => ({
        dim,
        value: s.id,
        label: s.name,
        sublabel: `${s.site} · ${s.teacher} · Gr ${s.grade}`,
        drillable: false,
        metrics: metricsFor([s]),
        delta: (hashStr(`student:${s.id}`) % 7) - 2,
      }));
    return rows;
  }

  // group scope students by the chosen dimension
  const groups = new Map<string, HStudent[]>();
  for (const s of students) {
    const k = key(s);
    const arr = groups.get(k);
    if (arr) arr.push(s);
    else groups.set(k, [s]);
  }

  const rows: BreakdownRow[] = [];
  for (const [value, members] of groups.entries()) {
    const m = metricsFor(members);
    let label = value;
    let sublabel = `${m.total.toLocaleString("en-US")} students`;
    if (dim === "grade") label = `Grade ${value}`;
    if (dim === "teacher" || dim === "coteacher") {
      sublabel = `${dominantSite(members)} · ${gradeRange(members)}`;
    }
    rows.push({
      dim,
      value,
      label,
      sublabel,
      drillable: true,
      metrics: m,
      delta: (hashStr(`${dim}:${value}`) % 7) - 2,
    });
  }

  // ordering: natural for grade/site/category; ranked by % on track for people
  if (dim === "grade") {
    rows.sort((a, b) => Number(a.value) - Number(b.value));
  } else if (dim === "site") {
    rows.sort(
      (a, b) =>
        SCHOOL_NAMES.indexOf(a.value as never) -
        SCHOOL_NAMES.indexOf(b.value as never),
    );
  } else if (dim === "category") {
    rows.sort(
      (a, b) =>
        CATEGORIES.indexOf(a.value as never) -
        CATEGORIES.indexOf(b.value as never),
    );
  } else {
    rows.sort((a, b) => b.metrics.onTrackPct - a.metrics.onTrackPct);
  }
  return rows;
}

// ---- Misc ------------------------------------------------------------------
export function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
